import { type ReactNode } from 'react'

export interface ModalProps {
  isOpen: boolean
  title: string
  children: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  onConfirm?: () => void
  onCancel?: () => void
  confirmVariant?: 'primary' | 'danger' | 'accent'
  closeOnBackdrop?: boolean
  // Everything is disabled while something is being saved.
  disabled?: boolean
  // Only the confirm button is disabled (the form is not ready yet); Cancel always works.
  confirmDisabled?: boolean
}

export default function Modal({
  isOpen,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  confirmVariant = 'primary',
  closeOnBackdrop = true,
  disabled = false,
  confirmDisabled = false,
}: ModalProps) {
  if (!isOpen) return null

  const handleBackdropClick = () => {
    if (closeOnBackdrop && onCancel && !disabled) {
      onCancel()
    }
  }

  const confirmClasses =
    confirmVariant === 'danger'
      ? 'bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed'
      : confirmVariant === 'accent'
        ? 'bg-[var(--accent-orange)] hover:bg-[var(--accent-orange-dark)] text-white disabled:opacity-50 disabled:cursor-not-allowed'
        : 'bg-[var(--forest-green)] hover:bg-[var(--emerald)] text-white disabled:opacity-50 disabled:cursor-not-allowed'

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleBackdropClick}
      />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <div className="shrink-0 px-6 py-4 border-b border-[var(--border)]">
          <h2 className="text-lg font-bold text-[var(--text-primary)]">{title}</h2>
        </div>

        <div className="px-6 py-5 text-[var(--text-secondary)] overflow-y-auto min-h-0">{children}</div>

        <div className="shrink-0 px-6 py-4 bg-[var(--bg-primary)] flex justify-end gap-3">
          {onCancel && (
            <button
              onClick={onCancel}
              disabled={disabled}
              className="px-4 py-2 rounded-lg border border-[var(--border)] bg-white text-[var(--text-secondary)] font-medium hover:bg-[var(--bg-tint)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelLabel}
            </button>
          )}
          {onConfirm && (
            <button
              onClick={onConfirm}
              disabled={disabled || confirmDisabled}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${confirmClasses}`}
            >
              {confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
